import { Menu, MenuButton, MenuItem, MenuList } from '@chakra-ui/menu'
import { useDisclosure } from '@chakra-ui/react'
import styled from '@emotion/styled'
import * as React from 'react'

import { BgColorProvider } from '~theme/BgColorProvider'
import { TActiveColorKey } from '~theme/typings'
import { AnimatedHeight } from '~ui/animatedHeight/AnimatedHeight'
import { Button } from '~ui/components/buttons/Button/Button'
import { IconChevronDown, IconChevronUp } from '~ui/components/icons/specific/IconChevrons'
import { Box, Grid } from '~ui/components/layout/Box'
import { List } from '~ui/components/List/List'
import { Text } from '~ui/components/typography/Text/Text'
import { radiuses } from '~ui/styleGuide/radiuses'
import { shadows } from '~ui/styleGuide/shadows'
import { spaces, TDoubleSpace } from '~ui/styleGuide/spaces'
import { THEME_KEY_ATTRIBUTE } from '~ui/themeDesignTools/config/themeDesignTools.config'

type TProps<TLanguage> = NoChildren & {
  languages: RoA<TLanguage>
  idPropKey: keyof TLanguage
  selectLabel: string
  onSelect: (language: TLanguage) => void
  renderLanguage: (language: TLanguage) => Defined<React.ReactNode>
  variant?: `popover` | `expandable`
  padding?: TDoubleSpace
}

const LIST_BG_COLOR: TActiveColorKey = `tertiary`

const StyledLanguageList = styled.div`
  background: ${({ theme }) => theme.activeColors[LIST_BG_COLOR].normal};
  border-radius: ${radiuses.small};
  overflow: hidden; /* hide item hover bg on 1st & last item corners */
  box-shadow: ${shadows.outer.lightLong};
`

type TStyledProps = {
  dividerColorValue: string
}

const propForwardOptions = {
  shouldForwardProp: (propName: string) => propName !== `dividerColorValue`,
}

const StyledLanguageButton = styled('button', propForwardOptions)<TStyledProps>`
  border: none;
  position: relative;
  background: transparent;

  cursor: pointer;
  transition: background 0.4s;

  padding: ${spaces.smallMedium} ${spaces.medium};

  /* separator between items (because border would be full width & we can’t use padding because of on-hover bg) */
  &:not(:first-of-type)::before {
    --separator-padding: ${spaces.small};

    content: '';
    height: 1px;
    width: calc(100% - 2 * var(--separator-padding));

    position: absolute;
    top: -1px;
    left: var(--separator-padding);

    background: ${({ dividerColorValue }) => dividerColorValue};
    transition: none; /* override the transition on list */
  }

  &:hover {
    background: ${({ theme }) => theme.activeColors[LIST_BG_COLOR].hover};

    &::before {
      background: transparent; /* hide the separator that is not hidden by the bg */
    }
  }
`

const getSelectButtonProps = (
  isOpen: boolean,
): Pick<PropsOf<typeof Button>, 'shadow' | 'rightIcon'> => ({
  shadow: `none`,
  rightIcon: { icon: isOpen ? IconChevronUp : IconChevronDown, size: `1rem`, contrast: `low` },
})

export const LanguagePicker = <TLanguage,>({ variant, ...props }: TProps<TLanguage>) => {
  if (variant === `expandable`) {
    return <LanguagePickerExpandable {...props} />
  }

  return <LanguagePickerPopover {...props} />
}

// TODO hover state bg is broken here, fix if needed

const LanguagePickerPopover = <TLanguage,>({
  onSelect,
  languages,
  idPropKey,
  selectLabel,
  renderLanguage,
  padding,
}: TProps<TLanguage>) => {
  return (
    <Menu placement={`bottom-end`} closeOnBlur>
      {({ isOpen }) => (
        <Grid width={`100%`}>
          <MenuButton
            as={Button}
            width={`100%`}
            variant={`ghost`}
            textAlign={`left`}
            padding={{ vertical: padding?.vertical ?? `none`, horizontal: `none` }}
            {...getSelectButtonProps(isOpen)}
          >
            <Text fontSize={`s14`}>{selectLabel}</Text>
          </MenuButton>

          <BgColorProvider bgColor={`activeColors.${LIST_BG_COLOR}.normal`}>
            {({ bgColor, dividerColors }) => {
              const themeDesignProps = { [THEME_KEY_ATTRIBUTE]: bgColor }

              return (
                <MenuList as={StyledLanguageList} {...themeDesignProps}>
                  {languages.map((language) => (
                    <MenuItem
                      as={StyledLanguageButton}
                      key={String(language[idPropKey])}
                      onClick={() => onSelect(language)}
                      dividerColorValue={dividerColors['normal']}
                    >
                      {renderLanguage(language)}
                    </MenuItem>
                  ))}
                </MenuList>
              )
            }}
          </BgColorProvider>
        </Grid>
      )}
    </Menu>
  )
}

/**
 * list of languages is added to the normal document flow
 * as it opens, it moves the content below
 */
const LanguagePickerExpandable = <TLanguage,>({
  idPropKey,
  languages,
  onSelect,
  renderLanguage,
  selectLabel,
  padding,
}: TProps<TLanguage>) => {
  const { isOpen, onToggle } = useDisclosure()

  return (
    <Grid width={`100%`}>
      <Button
        width={`100%`}
        variant={`ghost`}
        padding={padding}
        onClick={onToggle}
        {...getSelectButtonProps(isOpen)}
      >
        <Box flexGrow={1}>
          <Text textAlign={`left`}>{selectLabel}</Text>
        </Box>
      </Button>
      <AnimatedHeight isOpen={isOpen}>
        <Box>
          <List
            itemSpacing={`none`}
            idPropKey={idPropKey}
            items={languages}
            onItemClick={onSelect}
            renderItem={(language) => (
              <Button
                width={`100%`}
                variant={`ghost`}
                padding={padding}
                justifyContent={`flex-start`}
                key={String(language[idPropKey])}
              >
                {renderLanguage(language)}
              </Button>
            )}
          />
        </Box>
      </AnimatedHeight>
    </Grid>
  )
}
